import { API_BASE } from "../../lib/api.ts";
import { newOpId } from "../service/admin/agent-lifecycle-client.ts";

/**
 * Client for the Discovered agents screen (G1 #137). Reads go through `apiFetch` (see `use-discovered.ts`);
 * the two writes go to the BFF's orchestrating endpoints, which enforce the admin role, a reason and the
 * strict step-up before anything is forwarded. The shared `apiFetch` only surfaces `{error:{code}}` bodies,
 * so this client reads the body itself and returns the code, which is what the dialog needs to say something
 * useful. The signed invitation arrives once in a Register answer; it is held in component state only and
 * is never written to storage or a query cache.
 */

export type DiscoveredStatus = "new" | "registered" | "rejected";
export const DISCOVERED_STATUSES: readonly DiscoveredStatus[] = ["new", "registered", "rejected"];

export interface DiscoveredTool {
  readonly tool: string;
  readonly calls: number;
}
export interface DiscoveredGateway {
  readonly gateway_id: string;
  readonly calls: number;
}
export interface DiscoveredAgent {
  readonly caller_key: string;
  readonly first_seen: number;
  readonly last_seen: number;
  readonly call_count: number;
  readonly client_name?: string | null;
  readonly client_version?: string | null;
  readonly transport?: string | null;
  readonly source_addr?: string | null;
  readonly refusal_reason?: string | null;
  readonly status: DiscoveredStatus;
  readonly decided_by?: string | null;
  readonly decided_at?: number | null;
  readonly decision_reason?: string | null;
  readonly invitation_id?: string | null;
  readonly invitation_expires_at?: number | null;
  readonly tools: readonly DiscoveredTool[];
  readonly gateways: readonly DiscoveredGateway[];
}
export interface DiscoveredPage {
  readonly items: readonly DiscoveredAgent[];
  readonly next_cursor: string | null;
}

/** What Register returns: the recorded decision and, once, the signed invitation. */
export interface RegisterAnswer {
  readonly invitation: { readonly value: string; readonly invitation_id: string; readonly expires_at: number };
}

export { newOpId };

export type DecisionResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

function codeOf(parsed: unknown, status: number): string {
  if (typeof parsed === "object" && parsed !== null) {
    const error = (parsed as { error?: unknown }).error;
    if (typeof error === "string") return error;
    if (typeof error === "object" && error !== null && typeof (error as { code?: unknown }).code === "string") {
      return (error as { code: string }).code;
    }
  }
  return `http_${status}`;
}

export function decisionPath(callerKey: string, action: "register" | "reject"): string {
  return `${API_BASE}/discovered-agents/${encodeURIComponent(callerKey)}/${action}`;
}

/** Send one decision. Never throws for an HTTP answer; a network failure resolves `network`. */
export async function sendDecision<T>(
  callerKey: string,
  action: "register" | "reject",
  body: Readonly<Record<string, unknown>>,
  signal?: AbortSignal,
): Promise<DecisionResult<T>> {
  let response: Response;
  try {
    response = await fetch(decisionPath(callerKey, action), {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, code: codeOf(parsed, response.status) };
}

/** Plain-language text for a refusal code. Anything unknown gets a generic line, never a raw code. */
export function decisionMessage(code: string): string {
  switch (code) {
    case "mfa_required":
      return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required":
      return "Set up two-factor authentication on your account first. This action needs a second factor.";
    case "invalid_request":
    case "validation_failed":
      return "Add a reason of 1 to 256 characters.";
    case "forbidden":
      return "Only a team administrator can do this.";
    case "conflict":
      return "This caller was already rejected, or it changed while this dialog was open. Close it and reload the list.";
    case "not_found":
      return "That caller is not in your team's list any more.";
    case "register_not_recorded":
      return "The invitation was issued but the decision could not be recorded. Try again: the same invitation is returned.";
    case "service_unavailable":
    case "service_auth_failed":
    case "unavailable":
      return "The invitation service is not available right now. Try again in a moment.";
    case "op_id_conflict":
      return "This operation was already used for another caller. Close the dialog and start again.";
    case "network":
      return "The request could not be sent. Check your connection and try again.";
    default:
      return "The action could not be completed. Nothing was changed.";
  }
}
